import React from 'react';
import ColorInput from 'components/common/color-picker/ColorInput';
import { AutocompleteInput, DateTimePickerInput, SelectInput, TextInput } from 'components/forms/Inputs';
import { useRecordContext } from 'components/record/useRecordContext';
import { useController, useWatch } from 'react-hook-form';
import { useFetch } from 'hooks/useFetchData';
import { SubEventType } from 'types/App';

type UserType = { id: number; firstName: string; lastName: string; userDevice: { id: number; name: string } };

const CompetitorForm = (props: CompetitorFormProps) => {
  const { subEvent, devices = [] } = props;
  const { record } = useRecordContext();
  const isVirtual = !!record.isVirtual;

  const { data, loading } = useFetch<{ items: UserType[] }>('GET_LIST', 'User');
  const userOptions = React.useMemo(() => (data?.items || []).map((item) => ({ id: item.id, label: `${item.firstName} ${item.lastName}` })), [data]);

  const userId = useWatch({ name: 'userId' });

  const user = React.useMemo(() => data?.items?.find((item) => item.id === userId), [data, userId]);

  const devicesOptions = React.useMemo(() => {
    if (isVirtual && record.device) {
      return [{ id: record.device.id, label: record.device.name }];
    }
    return user?.userDevice ? [{ id: user.userDevice.id, label: user.userDevice.name }, ...devices] : devices;
  }, [isVirtual, record.device, user, devices]);

  const {
    field: { onChange: onChangeName }
  } = useController({ name: 'name' });

  const {
    field: { onChange: onChangeDeviceId }
  } = useController({ name: 'deviceId' });

  const {
    field: { onChange: onChangeUserId }
  } = useController({ name: 'userId' });

  const handleUserIdChange = React.useCallback(
    (_event: React.SyntheticEvent, value: unknown) => {
      const newUserId = value && typeof value === 'object' && 'id' in value ? (value as { id: number }).id : null;
      onChangeUserId(newUserId);
      const selectedUser = data?.items?.find((item) => item.id === newUserId);
      if (selectedUser) {
        onChangeName(`${selectedUser.firstName} ${selectedUser.lastName}`);
        if (subEvent.useGPS && selectedUser.userDevice) {
          onChangeDeviceId(selectedUser.userDevice.id);
        }
      }
    },
    [data, onChangeUserId, onChangeName, onChangeDeviceId, subEvent.useGPS]
  );

  const showDeviceSelect = subEvent.useGPS && (isVirtual || !loading);

  return (
    <React.Fragment>
      {!isVirtual && (
        <AutocompleteInput
          fullWidth
          loading={loading}
          name="userId"
          label="User"
          options={userOptions}
          autocompleteProps={{ onChange: handleUserIdChange }}
        />
      )}

      <TextInput required fullWidth label="Name" name="name" autoFocus disabled={!isVirtual && !!user} />
      <TextInput fullWidth label="Short Name" name="shortName" />
      <DateTimePickerInput fullWidth required format="YYYY-MM-DD HH:mm:ss" name="startTime" label="Start Time" />
      {subEvent.useGPS && <ColorInput fullWidth label="Color" name="color" />}
      {showDeviceSelect && (
        <SelectInput fullWidth required name="deviceId" label="Device" options={devicesOptions || []} disabled={isVirtual} />
      )}
      {subEvent.event.settings.useSI && !isVirtual && <TextInput fullWidth required name="siCard" label="SI Card" />}
    </React.Fragment>
  );
};

export default CompetitorForm;

type CompetitorFormProps = {
  subEvent: SubEventType;
  devices: {
    id: string | number;
    label: string | number;
    disabled?: boolean;
  }[];
};
